import {
  type Instant,
  type LocalDate,
  type Money,
  absMoney,
  formatLocalDate,
  fromApiMoney,
  negateMoney,
  parseInstant,
  parseLocalDate,
  signOfMoney,
  toApiMoney,
} from '@taler/domain'
import type { OperationQuery, Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'
import { projectQuery, type QueryProjection, queryFingerprint } from '../http/query.ts'
import type { TargetStatus } from './targets.ts'

export interface ForecastScenario {
  readonly id: string
  readonly name: string
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface CreateForecastScenarioInput {
  readonly name: string
}

/**
 * The contract carries no `frequency`/`type` on a variable - it is a single recurring flow,
 * applied monthly on `startDate`'s own day-of-month (clamped to a shorter month, mirroring
 * `LocalDate.plusMonths`) from `startDate` through `endDate` or the scenario's own projection
 * range when absent. A positive amount is income, a negative one an expense - the sign carries
 * direction, since the wire has no separate `kind`.
 */
export interface ForecastVariable {
  readonly id: string
  readonly scenarioId: string
  readonly name: string
  readonly amount: Money
  readonly startDate: LocalDate
  readonly endDate: LocalDate | undefined
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface AddForecastVariableInput {
  readonly name: string
  readonly amount: Money
  readonly startDate: LocalDate
  readonly endDate?: LocalDate
}

/** A one-off dated movement. Unlike a variable, the wire carries an explicit `kind` and requires
 * a strictly positive magnitude - `amount`'s own sign here is reconstructed from that `kind` at
 * the repository boundary so callers keep the same signed convention as a variable. */
export interface ForecastEvent {
  readonly id: string
  readonly scenarioId: string
  readonly name: string
  readonly amount: Money
  readonly date: LocalDate
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface AddForecastEventInput {
  readonly name: string
  readonly amount: Money
  readonly date: LocalDate
}

export interface ForecastPoint {
  readonly date: LocalDate
  readonly netWorth: Money
}

export interface ForecastProjection {
  readonly scenarioId: string
  readonly from: LocalDate
  readonly through: LocalDate
  readonly displayCurrency: string
  readonly points: readonly ForecastPoint[]
}

export interface ForecastTargetEvaluation {
  readonly asOf: LocalDate
  readonly status: TargetStatus
  /** Raw wire value - the caller already holds the target and knows its metric/currency to interpret it. */
  readonly currentValue: string | undefined
  readonly goalDatePassed: boolean
}

export type ForecastProjectionQuery = OperationQuery<'forecast-scenarios_projection'>
export type ForecastTargetEvaluationQuery = OperationQuery<'forecast-scenarios_targetEvaluations'>

const PROJECTION_PROJECTION: QueryProjection<ForecastProjectionQuery> = {
  from: (value) => value,
  through: (value) => value,
  displayCurrency: (value) => value,
}

const TARGET_EVALUATION_PROJECTION: QueryProjection<ForecastTargetEvaluationQuery> = {
  from: (value) => value,
  through: (value) => value,
}

export const forecastKeys = {
  scenarios: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'forecast', 'scenarios'),
  scenario: (ledgerId: string, scenarioId: string): CacheKey =>
    ledgerKey(ledgerId, 'forecast', 'scenarios', scenarioId, 'detail'),
  variables: (ledgerId: string, scenarioId: string): CacheKey =>
    ledgerKey(ledgerId, 'forecast', 'scenarios', scenarioId, 'variables'),
  events: (ledgerId: string, scenarioId: string): CacheKey =>
    ledgerKey(ledgerId, 'forecast', 'scenarios', scenarioId, 'events'),
  projection: (ledgerId: string, scenarioId: string, query: ForecastProjectionQuery): CacheKey =>
    ledgerKey(
      ledgerId,
      'forecast',
      'scenarios',
      scenarioId,
      'projection',
      queryFingerprint(projectQuery(query, PROJECTION_PROJECTION)),
    ),
  targetEvaluation: (
    ledgerId: string,
    scenarioId: string,
    targetId: string,
    query: ForecastTargetEvaluationQuery,
  ): CacheKey =>
    ledgerKey(
      ledgerId,
      'forecast',
      'scenarios',
      scenarioId,
      'target-evaluations',
      targetId,
      queryFingerprint(projectQuery(query, TARGET_EVALUATION_PROJECTION)),
    ),
  /** A scenario write (create, delete) makes the whole list stale. */
  invalidatedByScenarioWrite: (ledgerId: string): Invalidations => [
    ledgerKey(ledgerId, 'forecast', 'scenarios'),
  ],
  /** A variable or event write invalidates its own list plus every projection and target evaluation computed from it. */
  invalidatedByDriverWrite: (ledgerId: string, scenarioId: string): Invalidations => [
    ledgerKey(ledgerId, 'forecast', 'scenarios', scenarioId),
  ],
} as const

export interface ForecastRepository {
  listScenarios(ledgerId: string, signal?: AbortSignal): Promise<readonly ForecastScenario[]>
  getScenario(ledgerId: string, scenarioId: string, signal?: AbortSignal): Promise<ForecastScenario>
  createScenario(ledgerId: string, input: CreateForecastScenarioInput): Promise<ForecastScenario>
  deleteScenario(ledgerId: string, scenarioId: string): Promise<void>
  listVariables(
    ledgerId: string,
    scenarioId: string,
    signal?: AbortSignal,
  ): Promise<readonly ForecastVariable[]>
  createVariable(
    ledgerId: string,
    scenarioId: string,
    input: AddForecastVariableInput,
  ): Promise<ForecastVariable>
  deleteVariable(ledgerId: string, scenarioId: string, variableId: string): Promise<void>
  listEvents(ledgerId: string, scenarioId: string, signal?: AbortSignal): Promise<readonly ForecastEvent[]>
  createEvent(ledgerId: string, scenarioId: string, input: AddForecastEventInput): Promise<ForecastEvent>
  deleteEvent(ledgerId: string, scenarioId: string, eventId: string): Promise<void>
  projection(
    ledgerId: string,
    scenarioId: string,
    query: ForecastProjectionQuery,
    signal?: AbortSignal,
  ): Promise<ForecastProjection>
  targetEvaluation(
    ledgerId: string,
    scenarioId: string,
    targetId: string,
    query: ForecastTargetEvaluationQuery,
    signal?: AbortSignal,
  ): Promise<ForecastTargetEvaluation>
}

/** `toApiMoney`'s `currency: string` already carries a `Money`-validated `CurrencyCode`; only the generated schema's literal union needs the cast. */
function apiMoney(value: Money): Schemas['ApiMoney'] {
  return toApiMoney(value) as Schemas['ApiMoney']
}

export function createForecastRepository(client: TalerClient): ForecastRepository {
  const scenariosBase = (ledgerId: string) =>
    `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/forecast-scenarios`
  const scenario = (ledgerId: string, scenarioId: string) =>
    `${scenariosBase(ledgerId)}/${encodeURIComponent(scenarioId)}`
  const variablesBase = (ledgerId: string, scenarioId: string) =>
    `${scenario(ledgerId, scenarioId)}/variables`
  const variable = (ledgerId: string, scenarioId: string, variableId: string) =>
    `${variablesBase(ledgerId, scenarioId)}/${encodeURIComponent(variableId)}`
  const eventsBase = (ledgerId: string, scenarioId: string) => `${scenario(ledgerId, scenarioId)}/events`
  const event = (ledgerId: string, scenarioId: string, eventId: string) =>
    `${eventsBase(ledgerId, scenarioId)}/${encodeURIComponent(eventId)}`

  return {
    async listScenarios(ledgerId, signal) {
      const response = await client.call<Schemas['ScenarioResponse'][]>({
        method: 'GET',
        path: scenariosBase(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map(toScenario)
    },

    async getScenario(ledgerId, scenarioId, signal) {
      return toScenario(
        await client.call<Schemas['ScenarioResponse']>({
          method: 'GET',
          path: scenario(ledgerId, scenarioId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async createScenario(ledgerId, input) {
      return toScenario(
        await client.call<Schemas['ScenarioResponse'], Schemas['CreateScenarioRequest']>({
          method: 'POST',
          path: scenariosBase(ledgerId),
          body: { name: input.name },
        }),
      )
    },

    async deleteScenario(ledgerId, scenarioId) {
      await client.call<undefined>({ method: 'DELETE', path: scenario(ledgerId, scenarioId) })
    },

    async listVariables(ledgerId, scenarioId, signal) {
      const response = await client.call<Schemas['VariableResponse'][]>({
        method: 'GET',
        path: variablesBase(ledgerId, scenarioId),
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map(toVariable)
    },

    async createVariable(ledgerId, scenarioId, input) {
      const body: Schemas['AddVariableRequest'] = {
        name: input.name,
        amount: apiMoney(input.amount),
        startDate: formatLocalDate(input.startDate),
      }
      if (input.endDate !== undefined) body.endDate = formatLocalDate(input.endDate)
      return toVariable(
        await client.call<Schemas['VariableResponse'], Schemas['AddVariableRequest']>({
          method: 'POST',
          path: variablesBase(ledgerId, scenarioId),
          body,
        }),
      )
    },

    async deleteVariable(ledgerId, scenarioId, variableId) {
      await client.call<undefined>({ method: 'DELETE', path: variable(ledgerId, scenarioId, variableId) })
    },

    async listEvents(ledgerId, scenarioId, signal) {
      const response = await client.call<Schemas['EventResponse'][]>({
        method: 'GET',
        path: eventsBase(ledgerId, scenarioId),
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map(toEvent)
    },

    async createEvent(ledgerId, scenarioId, input) {
      const body: Schemas['AddEventRequest'] = {
        name: input.name,
        amount: apiMoney(absMoney(input.amount)),
        date: formatLocalDate(input.date),
        kind: signOfMoney(input.amount) < 0 ? 'EXPENSE' : 'INCOME',
      }
      return toEvent(
        await client.call<Schemas['EventResponse'], Schemas['AddEventRequest']>({
          method: 'POST',
          path: eventsBase(ledgerId, scenarioId),
          body,
        }),
      )
    },

    async deleteEvent(ledgerId, scenarioId, eventId) {
      await client.call<undefined>({ method: 'DELETE', path: event(ledgerId, scenarioId, eventId) })
    },

    async projection(ledgerId, scenarioId, query, signal) {
      const response = await client.call<Schemas['ProjectionResponse']>({
        method: 'GET',
        path: `${scenario(ledgerId, scenarioId)}/projection`,
        query: projectQuery(query, PROJECTION_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return {
        scenarioId: response.scenarioId,
        from: parseLocalDate(response.from),
        through: parseLocalDate(response.through),
        displayCurrency: response.displayCurrency,
        points: response.points.map((point) => ({
          date: parseLocalDate(point.date),
          netWorth: fromApiMoney(point.netWorth),
        })),
      }
    },

    async targetEvaluation(ledgerId, scenarioId, targetId, query, signal) {
      const response = await client.call<Schemas['TargetEvaluationResponse']>({
        method: 'GET',
        path: `${scenario(ledgerId, scenarioId)}/target-evaluations/${encodeURIComponent(targetId)}`,
        query: projectQuery(query, TARGET_EVALUATION_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return {
        asOf: parseLocalDate(response.asOf),
        status: response.status,
        currentValue: response.currentValue,
        goalDatePassed: response.goalDatePassed,
      }
    },
  }
}

function toScenario(response: Schemas['ScenarioResponse']): ForecastScenario {
  return {
    id: response.id,
    name: response.name,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}

function toVariable(response: Schemas['VariableResponse']): ForecastVariable {
  return {
    id: response.id,
    scenarioId: response.scenarioId,
    name: response.name,
    amount: fromApiMoney(response.amount),
    startDate: parseLocalDate(response.startDate),
    endDate: response.endDate === undefined ? undefined : parseLocalDate(response.endDate),
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}

function toEvent(response: Schemas['EventResponse']): ForecastEvent {
  const magnitude = fromApiMoney(response.amount)
  return {
    id: response.id,
    scenarioId: response.scenarioId,
    name: response.name,
    amount: response.kind === 'EXPENSE' ? negateMoney(magnitude) : magnitude,
    date: parseLocalDate(response.date),
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}
